"use client";

import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { interpolateText } from "@lib/utils/interpolateText";
import { isEmpty } from "lodash-es";
import { createContext, useContext } from "react";
import type {
    MicrocopyContextAPI,
    MicrocopyContextState,
    MicrocopyGetOptions,
    MicrocopySetKey,
} from "./types";
import { LogMicrocopyError, LogMicrocopyType } from "./types";

const MicrocopyContext = createContext<MicrocopyContextState | undefined>(undefined);
const { Provider: MicrocopyProvider } = MicrocopyContext;

const loggedMissingKeys = new Set<string>(); // Set is used to track unique missing microcopy keys to avoid logging the same key multiple times
export const MAX_LOGGED_KEYS = 1000; // Limit the number of logged keys to prevent memory issues
export const loggedKeyQueue: string[] = [];

const useMicrocopy = (): MicrocopyContextAPI => {
    const context = useContext(MicrocopyContext);

    if (context === undefined) {
        throw new Error("useMicrocopy must be within MicrocopyProvider");
    }

    const get = (
        microcopySetKey: MicrocopySetKey,
        microcopyKey: string,
        options?: MicrocopyGetOptions
    ): string | null => {
        const microcopySet = context[microcopySetKey];
        const mcType = options?.fromMultiple
            ? LogMicrocopyType.GET_MULTIPLE
            : LogMicrocopyType.GET_SINGLE;

        if (!microcopySet) {
            logMissingMicrocopy(
                microcopyKey,
                microcopySetKey,
                mcType,
                LogMicrocopyError.MISSING_MICROCOPY_SET
            );
            return null;
        }

        const result = microcopySet[microcopyKey];

        if (!result) {
            // Remove error logs from unit tests
            // eslint-disable-next-line turbo/no-undeclared-env-vars
            if (!process.env.JEST_WORKER_ID && !options?.fromMultiple) {
                logMissingMicrocopy(
                    microcopyKey,
                    microcopySetKey,
                    mcType,
                    LogMicrocopyError.MISSING_MICROCOPY
                );
            }
            return null;
        }

        return isEmpty(options?.replacements)
            ? result.value
            : interpolateText(result.value, options.replacements);
    };

    const getMultiple = (
        microcopySetKey: MicrocopySetKey,
        microcopyKeys: string[],
        options?: MicrocopyGetOptions
    ): Record<string, string> | null => {
        const result: Record<string, string> = {};
        const getOptions = { ...(options || {}), fromMultiple: true };
        microcopyKeys.forEach((key) => {
            const microcopy = get(microcopySetKey, key, getOptions);

            if (microcopy) {
                result[key] = microcopy;
            }
        });
        return result;
    };

    return {
        get,
        getMultiple,
    };
};

function logMissingMicrocopy(
    key: string,
    set: string,
    mcType: LogMicrocopyType,
    type: LogMicrocopyError
): void {
    const uniqueId = `${set}:${key}`;

    if (loggedMissingKeys.has(uniqueId) || process.env.NEXT_PUBLIC_ENVIRONMENT !== "production") {
        return;
    }

    // Maintain max size of unique keys to log.
    // If the limit is reached, remove the oldest key from the set and queue.
    // This ensures we don't keep growing the set indefinitely and only log the most recent missing keys
    if (loggedMissingKeys.size >= MAX_LOGGED_KEYS) {
        const oldestKey = loggedKeyQueue.shift();
        if (oldestKey) loggedMissingKeys.delete(oldestKey);
    }

    loggedMissingKeys.add(uniqueId);
    loggedKeyQueue.push(uniqueId);

    Logger.warn(ServiceType.CONTENTFUL, `Missing microcopy "${key}" in "${set}"`, {
        tag: LogTag.MICROCOPY,
        key,
        set,
        pathName: typeof window !== "undefined" ? window.location.pathname : "",
        type: mcType,
        error: {
            name: type,
        },
    });
}

export { MicrocopyContext, MicrocopyProvider, useMicrocopy };
